import React, { useCallback, useState } from "react";
import { Text } from "@geist-ui/core";
import { Badge, Button, Flex, TextArea } from "@radix-ui/themes";
import { TrashIcon, PlusIcon } from "@radix-ui/react-icons";

import { useSimulatorTracking } from "@/routes/simulator/useSimulatorTracking";
import { SimulatorChannel } from "@/v2/entities/simulator/Simulator.model";
import { Prerequisite } from "@/v2/entities/prerequisite/Prerequisite.model";

import { useSimulator } from "../../context/SimulatorWrapper";

import styles from "./styles.module.scss";

interface InstructionsPrerequisitesProps {
  topicId: number;
  prerequisites: Prerequisite[];
  trackingData: any;
}

export const InstructionsPrerequisites: React.FC<
  InstructionsPrerequisitesProps
> = ({ topicId, prerequisites }) => {
  const { createPrerequisite, deletePrerequisite, simulator } = useSimulator();
  const { trackInstructionAdded } = useSimulatorTracking();

  const [newContent, setNewContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const handleCancelAdd = useCallback(() => {
    setIsAdding(false);
    setNewContent("");
  }, []);

  const handleAddPrerequisite = useCallback(async () => {
    const content = newContent.trim();
    if (!content || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      await createPrerequisite(content, topicId);
      trackInstructionAdded(content);
      setNewContent("");
      setIsAdding(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    newContent,
    isSubmitting,
    createPrerequisite,
    topicId,
    trackInstructionAdded,
  ]);

  const handlePrimaryAction = useCallback(() => {
    if (!isAdding) {
      setIsAdding(true);
      return;
    }
    handleAddPrerequisite();
  }, [isAdding, handleAddPrerequisite]);

  if (simulator?.channel !== SimulatorChannel.autonomous_quackchat) {
    return null;
  }

  return (
    <div className={styles.briefSection}>
      <Flex justify="between" align="center" mb="4">
        <Flex align="center" gap="2">
          <Text style={{ fontSize: "14px", fontWeight: 500 }}>
            Pre-requisites
          </Text>
          <Badge
            size="1"
            variant="solid"
            style={{
              backgroundColor: "var(--gray-4)",
              color: "var(--gray-12)",
            }}
          >
            {prerequisites?.length ?? 0}
          </Badge>
        </Flex>
      </Flex>

      <div className={styles.instructionsTable}>
        {prerequisites?.map((prerequisite, index) => (
          <div key={index} className={styles.instructionRow}>
            <Flex gap="2" justify="between" style={{ width: "100%" }}>
              <Flex gap="2" align="start">
                <div className={styles.instructionContent}>
                  {prerequisite.content}
                </div>
              </Flex>
              <Button
                variant="ghost"
                color="gray"
                size="1"
                onClick={async () => {
                  trackInstructionAdded(prerequisite.content);
                  await deletePrerequisite(prerequisite.id);
                }}
              >
                <TrashIcon style={{ width: "14px", height: "14px" }} />
              </Button>
            </Flex>
          </div>
        ))}
      </div>

      <Flex direction="column" gap="2" mt="3">
        {isAdding && (
          <TextArea
            autoFocus
            size="2"
            style={{ width: "100%", minHeight: "96px" }}
            placeholder="Add a pre-requisite..."
            value={newContent}
            onChange={(event) => setNewContent(event.target.value)}
          />
        )}
        <Flex gap="2" justify="end">
          {isAdding && (
            <Button
              size="2"
              variant="soft"
              color="gray"
              disabled={isSubmitting}
              onClick={handleCancelAdd}
            >
              Cancel
            </Button>
          )}
          <Button
            size="2"
            variant="soft"
            disabled={isAdding && (!newContent.trim() || isSubmitting)}
            onClick={handlePrimaryAction}
          >
            <PlusIcon style={{ width: "14px", height: "14px" }} />
            Add
          </Button>
        </Flex>
      </Flex>
    </div>
  );
};
